import { faChevronDown, faChevronUp } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Checkbox, Collapse, IconButton, TableCell, TableRow, Typography } from "@mui/material"
import { translate } from "app/language/service"
import { IForm } from "form/model"
import { getFilteredOperators } from "form/tools/operator"
import { getOperatorRows } from "form/tools/operator-forecast"
import { synchronizeNewSspSelection } from "form/tools/ssp"
import { getUmbracityRow, isUmbracitySsp } from "form/tools/ssp-operator-umbracity"
import React from "react"
import { ISsp } from "ssp/model"
import { FlexBox } from "ui/components/FlexBox"
import { OperatorList } from "./OperatorList"

interface ISspOperatorRowProps {
  form: IForm
  ssp: ISsp
  index: number
  isSelected: boolean
  disabled: boolean
  rowRenderIndex: number
  setSelectedSspIds: (selectedIds: number[]) => any
}

export const SspOperatorRow = React.memo(
  ({ form, ssp, index, isSelected, disabled, rowRenderIndex, setSelectedSspIds }: ISspOperatorRowProps) => {
    const [open, setOpen] = React.useState(false)
    const allOperators = form.screens.allOperators.filter(o => o.ssp_id === ssp.id)
    const selectedOperators = form.screens.operators.filter(o => o.ssp_id === ssp.id)
    const isUmbracity = isUmbracitySsp(ssp)
    const filteredOperatorRows = !isUmbracity
      ? getOperatorRows(
          getFilteredOperators(
            allOperators,
            form.screens.operatorFilter,
            form.screens.operatorSelectFilter,
            selectedOperators
          )
        )
      : [getUmbracityRow(ssp.id)] // is filtered by parent component as ssp contain only one row
    return (
      <>
        <TableRow
          sx={{
            "& > *": { borderBottom: "unset" }
          }}>
          <TableCell width="1rem">
            <Checkbox
              id={`ssp-row-${index}-select`}
              disabled={disabled}
              checked={isSelected}
              indeterminate={
                isSelected && selectedOperators.length !== 0 && allOperators.length !== selectedOperators.length
              }
              onChange={(_event, checked) => {
                const newSelectedIds = checked
                  ? [...form.screens.sspIds, ssp.id]
                  : form.screens.sspIds.filter(id => id !== ssp.id)
                synchronizeNewSspSelection(newSelectedIds)
                setSelectedSspIds(newSelectedIds)
              }}
            />
          </TableCell>
          <TableCell component="th" scope="row" onClick={() => setOpen(!open)} sx={{ cursor: "pointer" }}>
            <FlexBox jc="start" gap>
              <Typography id={`ssp-row-${index}-name`} fontSize=".9rem" fontWeight={400} color="textPrimary">
                {ssp.name}
              </Typography>
              <Typography id={`ssp-row-${index}-text`} fontSize=".9rem" fontWeight={400} color="textSecondary">
                {!isSelected
                  ? selectedOperators.length !== 0
                    ? translate("operators.ignored", selectedOperators.length)
                    : null
                  : selectedOperators.length === 0 || selectedOperators.length === allOperators.length
                    ? translate("operators.all.included")
                    : translate("operators.included.count", `${selectedOperators.length} / ${allOperators.length}`)}
              </Typography>
            </FlexBox>
          </TableCell>
          <TableCell width="1rem">
            <IconButton
              id={`ssp-row-${index}-expand-icon`}
              aria-label="expand row"
              size="small"
              onClick={() => setOpen(!open)}>
              <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} />
            </IconButton>
          </TableCell>
        </TableRow>
        <TableRow sx={{ p: "0 !important" }}>
          <TableCell sx={{ p: "0 !important", borderBottom: "none" }} colSpan={6}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <OperatorList
                form={form}
                sspIndex={index}
                rowRenderIndex={rowRenderIndex}
                allOperators={allOperators}
                selectedOperators={selectedOperators}
                isSspSelected={isSelected}
                filteredOperatorRows={filteredOperatorRows}
                isUmbracity={isUmbracity}
              />
            </Collapse>
          </TableCell>
        </TableRow>
      </>
    )
  }
)
